import React, { Component, ReactElement, } from "react";

// add other imports
import { classNames, } from "components/Utils";
import {
    Button,
    ButtonColor,
    ButtonProps,
    ButtonStyle,
    Checkbox,
    DropdownProps,
    NumberInput,
    SplitButton,
} from "components/FormControls";
import {
    Icons,
    Results,
    PoolRollResult,
    SingleRollResult,
    rollPool,
    rollSingle,
    RollType,
    PoolRoll,
    RerollCard,
    getRollHistoryCount,
    storeRollHistory,
} from ".";

// styling
import "./style.scss";
import { HistoryModal } from "./HistoryModal";

// enums
enum RouseButtonAction {
    Rouse = 1,
    D10,
}

// interfaces
interface State {
    // values
    poolValue: number;
    hungerValue: number;
    difficultyValue: number;

    // component states
    isHungerDisabled: boolean;
    isDifficultyDisabled: boolean;
    isRollDisabled: boolean;
    isRouseDisabled: boolean;
    isRerollCardVisible: boolean;
    isRollHistoryModalVisible: boolean;

    // roll results
    rollResult?: PoolRollResult | SingleRollResult;

    // component actions
    rouseButtonAction: RouseButtonAction;

    // roll logs
    rollHistoryCount?: number;
}

export class DiceRoller extends Component<{}, State> {
    private _defaultPool = 2;
    private _minPool = 1;
    private _maxPool = 20;

    private _defaultHunger = 1;
    private _minHunger = 0;
    private _maxHunger = 5;

    private _defaultDifficulty = 1;
    private _minDifficulty = 1;
    private _maxDifficulty = 100;

    private _rollTimerId: number | null = null;
    private _rollTimeout = 1000; // 1 second (1000ms) between clicks to prevent accidental triggering

    private _rouseTimerId: number | null = null;
    private _rouseTimeout = 1000; // 1 second (1000ms) between clicks to prevent accidental triggering

    constructor(props: any) {
        super(props);

        this.state = {
            poolValue: this._defaultPool,
            hungerValue: this._defaultHunger,
            difficultyValue: this._defaultDifficulty,

            isHungerDisabled: false,
            isDifficultyDisabled: true,
            isRollDisabled: false,
            isRouseDisabled: false,
            isRerollCardVisible: false,
            isRollHistoryModalVisible: false,

            rouseButtonAction: RouseButtonAction.Rouse,

            rollHistoryCount: getRollHistoryCount(),
        };

        // method binding
        this._onChangePool = this._onChangePool.bind(this);
        this._onChangeHunger = this._onChangeHunger.bind(this);
        this._onChangeDifficulty = this._onChangeDifficulty.bind(this);
        this._onClickRollButton = this._onClickRollButton.bind(this);
        this._onClickRouseButton = this._onClickRouseButton.bind(this);
        this._handleReroll = this._handleReroll.bind(this);
        this._toggleRollHistoryModal = this._toggleRollHistoryModal.bind(this);
    }

    public render(): ReactElement {
        const { rollResult, } = this.state;

        const mainCss = classNames([
            "__diceroller__",
        ]);

        const inputCss = classNames([
            "col-md-4",
            "mb-3",
            "__input__",
        ]);

        return (
            <div className={mainCss}>
                <div className="__rollui__">
                    {/* number inputs */}
                    <div className="row mb-3">
                        <div className={inputCss}>
                            {this._renderPoolInput()}
                        </div>

                        <div className={inputCss}>
                            {this._renderHungerInput()}
                        </div>

                        <div className={inputCss}>
                            {this._renderDifficulty()}
                        </div>
                    </div>

                    {/* action buttons */}
                    <div className="row">
                        <div className="col-12 col-sm-6 col-md-7 mb-3">
                            {this._renderRollButton()}
                        </div>

                        <div className="col-12 col-sm-6 col-md-5 mb-3 d-flex">
                            {this._renderRouseButton()}
                            {this._renderRollHistoryButton()}
                        </div>
                    </div>
                </div>

                <Results rollResult={rollResult} onReroll={this._handleReroll} />
            </div>
        );
    }

    /* Timers */
    public componentWillUnmount() {
        this._clearTimers();
    }

    private _clearTimers() {
        if (this._rollTimerId) {
            window.clearTimeout(this._rollTimerId);
            this._rollTimerId = null;
        }

        if (this._rouseTimerId) {
            window.clearTimeout(this._rouseTimerId);
            this._rouseTimerId = null;
        }
    }

    /* Pool */
    private _renderPoolInput(): ReactElement {
        const { poolValue, } = this.state;
        return (
            <>
                <label className="form-label">Pool</label>
                <NumberInput
                    value={poolValue}
                    min={this._minPool}
                    max={this._maxPool}
                    defaultValue={this._defaultPool}
                    showChangeButtons={true}
                    onChange={this._onChangePool}
                />
            </>
        );
    }

    private _onChangePool(n: number): void {
        this.setState({
            poolValue: n,
        });
    }

    /* Hunger */
    private _renderHungerInput(): ReactElement {
        const { hungerValue, isHungerDisabled, } = this.state;

        const handleToggle = () => {
            this.setState({
                isHungerDisabled: !isHungerDisabled,
            });
        };

        return (
            <>
                <Checkbox label="Hunger" checked={!isHungerDisabled} onChange={handleToggle} />
                <NumberInput
                    value={hungerValue}
                    min={this._minHunger}
                    max={this._maxHunger}
                    defaultValue={this._defaultHunger}
                    showChangeButtons={true}
                    onChange={this._onChangeHunger}
                    disabled={isHungerDisabled}
                />
            </>
        );
    }

    private _onChangeHunger(n: number): void {
        this.setState({
            hungerValue: n,
        });
    }

    /* Difficulty */
    private _renderDifficulty(): ReactElement {
        const { difficultyValue, isDifficultyDisabled, } = this.state;

        const handleToggle = () => {
            this.setState({
                isDifficultyDisabled: !isDifficultyDisabled,
            });
        };

        return (
            <>
                <Checkbox label="Difficulty" checked={!isDifficultyDisabled} onChange={handleToggle} />
                <NumberInput
                    value={difficultyValue}
                    min={this._minDifficulty}
                    max={this._maxDifficulty}
                    defaultValue={this._defaultDifficulty}
                    showChangeButtons={true}
                    onChange={this._onChangeDifficulty}
                    disabled={isDifficultyDisabled}
                />
            </>
        );
    }

    private _onChangeDifficulty(n: number): void {
        this.setState({
            difficultyValue: n,
        });
    }

    /* Roll button */
    private _renderRollButton(): ReactElement {
        const { isRollDisabled, } = this.state;
        const disabled = isRollDisabled || this._getIsRollDisabled();

        const buttonColor = disabled ? ButtonColor.Secondary : undefined;

        return (
            <Button
                label="Roll"
                className={"w-100"}
                onClick={this._onClickRollButton}
                disabled={disabled}
                color={buttonColor}
            />
        );
    }

    private _onClickRollButton(): void {
        const { poolValue, hungerValue, difficultyValue, isHungerDisabled, isDifficultyDisabled, } = this.state;

        // create the roll config
        const config = {
            type: RollType.Pool,
            pool: poolValue,
            hunger: isHungerDisabled ? undefined : hungerValue,
            difficulty: isDifficultyDisabled ? undefined : difficultyValue,
        } as PoolRoll;

        // perform the actual roll
        const rollResult = rollPool(config);

        // store the roll to localstorage
        storeRollHistory(rollResult);

        // update state
        this.setState({
            rollResult, // add result to state
            isRollDisabled: true, // temporarily disable the button
            isRerollCardVisible: true, // show the re-roll option
        }, () => {
            this._rollTimerId = window.setTimeout(() => {
                this._rollTimerId = null;
                this.setState({
                    isRollDisabled: false,
                });
            }, this._rollTimeout);
            this._updateRollHistoryCount();
        });
    }

    // Checks the values of pool & hunger
    private _getIsRollDisabled(): boolean {
        const { poolValue, hungerValue, isHungerDisabled, } = this.state;
        return (poolValue === 0 && (hungerValue === 0 || isHungerDisabled));
    }

    /* Rouse button */
    private _renderRouseButton(): ReactElement {
        const { isRouseDisabled, rouseButtonAction, } = this.state;

        const buttonLabel = rouseButtonAction === RouseButtonAction.D10 ? "D10" : "Rouse";
        const buttonProps = {
            label: buttonLabel,
            onClick: this._onClickRouseButton,
            className: "w-100",
        } as ButtonProps;

        const dropdownProps = {
            items: this._renderRouseDropdownItems(),
            dismissOnClick: true,
        } as DropdownProps;

        const containerCss = classNames([
            "w-100",
            "__rousebuttoncontainer__"
        ]);

        const color = isRouseDisabled ? ButtonColor.Secondary : undefined;

        return (
            <SplitButton
                buttonProps={buttonProps}
                className={containerCss}
                dropdownProps={dropdownProps}
                disabled={isRouseDisabled}
                color={color}
            />
        );
    }

    private _renderRouseDropdownItems(): JSX.Element[] {
        const handleMenuItemClick = (e: React.FormEvent<HTMLButtonElement>) => {
            const name = e.currentTarget.name;
            const action = name === "d10" ? RouseButtonAction.D10 : RouseButtonAction.Rouse;

            // TODO: automatically trigger the action on menu item select?
            this.setState({
                rouseButtonAction: action,
            });
        };

        const createItem = (label: string) => {
            const { rouseButtonAction, } = this.state;
            const name = label.toLowerCase();

            let selected = false;
            if (name === "rouse" && rouseButtonAction === RouseButtonAction.Rouse) {
                selected = true;
            } else if (name === "d10" && rouseButtonAction === RouseButtonAction.D10) {
                selected = true;
            }

            const css = classNames([
                "dropdown-item",
                "__menuitem__",
                selected ? "selected" : "",
            ]);
            return (
                <li key={name}>
                    <button
                        className={css}
                        onClick={handleMenuItemClick}
                        name={name}
                        disabled={selected}
                    >
                        <span>{label}</span>
                    </button>
                </li>
            );
        };

        const items = [
            createItem("Rouse"),
            createItem("D10"),
        ];

        return items;
    }

    private _onClickRouseButton(): void {
        const { rouseButtonAction, } = this.state;

        let rollResult: SingleRollResult;
        if (rouseButtonAction === RouseButtonAction.D10) {
            rollResult = rollSingle({
                type: RollType.D10,
            });
        } else {
            rollResult = rollSingle({
                type: RollType.Rouse,
            });
        }

        // update state
        this.setState({
            isRouseDisabled: true, // temporarily disable the button
            rollResult, // add result to state
        }, () => {
            this._rouseTimerId = window.setTimeout(() => {
                this._rouseTimerId = null;
                this.setState({
                    isRouseDisabled: false,
                });
            }, this._rouseTimeout);
            this._updateRollHistoryCount();
        });

        // store the roll to localstorage
        storeRollHistory(rollResult);
    }

    private _renderRerollCard(): ReactElement {
        const { rollResult, isRerollCardVisible, } = this.state;

        if (!rollResult || rollResult.type !== RollType.Pool || !isRerollCardVisible) {
            return (<></>);
        }

        return (
            <RerollCard result={rollResult as PoolRollResult} onReroll={this._handleReroll} />
        );
    }

    private _handleReroll(pool: number[]): void {
        const newResult = this._getNewPoolResult(pool);
        // update state and hide the re-roll card
        this.setState({
            rollResult: newResult,
            isRerollCardVisible: false,
        }, () => {
            this._updateRollHistoryCount();
        });

        storeRollHistory(newResult);
    }

    private _getNewPoolResult(pool: number[]): PoolRollResult {
        const { rollResult, } = this.state;

        // merge the elements together
        return {
            ...rollResult,
            pool,
            timestamp: Date.now(),
            reroll: true,
        } as PoolRollResult;
    }

    /* roll log */
    private _updateRollHistoryCount(): void {
        const rollLogs = getRollHistoryCount();
        this.setState({
            rollHistoryCount: rollLogs,
        });
    }

    private _toggleRollHistoryModal(): void {
        const { isRollHistoryModalVisible, } = this.state;
        this.setState({
            isRollHistoryModalVisible: !isRollHistoryModalVisible,
        });
    }

    private _renderRollHistoryButton(): ReactElement {
        const { rollHistoryCount, } = this.state;

        const label = "Roll History";
        const isDisabled = !rollHistoryCount;
        const color = isDisabled ? ButtonColor.Secondary : undefined;
        const css = classNames([
            "ms-3",
            "ms-sm-4",
            "__rollhistorybutton__",
            isDisabled ? "disabled" : "",
        ]);

        return (
            <>
                <Button
                    className={css}
                    onClick={this._toggleRollHistoryModal}
                    disabled={isDisabled}
                    style={ButtonStyle.Outline}
                    color={color}
                >
                    {Icons.History}
                    <span className="visually-hidden">{label}</span>
                </Button>

                {this._renderRollHistoryModal()}
            </>
        );
    }

    private _renderRollHistoryModal(): ReactElement {
        const { isRollHistoryModalVisible, } = this.state;

        if (!isRollHistoryModalVisible) {
            return <></>;
        }

        return (
            <HistoryModal onCancel={this._toggleRollHistoryModal} />
        );
    };
}

